import type { ScanType } from './searchList';

export const CAPTURE_LEAD_MODULES = ['emails', 'phones', 'hunter'] as const;

/**
 * Opt-in por execução do corte 2. Não é capability persistente da extensão: o comando vivo de
 * navegação declara o protocolo e a extensão o vincula ao `captureRunId` daquela execução.
 */
export const CAPTURE_EVENTS_PROTOCOL = 'capture-events-v1' as const;

export const CAPTURE_ACK_ERROR_CODES = [
  'invalid_batch',
  'no_active_list',
  'email_dns_unavailable',
  'quota_exceeded',
  'storage_failed',
  'internal_error',
] as const;

export type CaptureLeadModule = (typeof CAPTURE_LEAD_MODULES)[number];
export type CaptureAckErrorCode = (typeof CAPTURE_ACK_ERROR_CODES)[number];
export type CaptureTarget = 'panel' | 'backend';
export type CaptureRawRecord = Record<string, unknown>;

interface CaptureBatchBase {
  /** Destino imutável do lote, definido pelo painel ao iniciar a captura. */
  listId: string;
  /** Identidade da execução; ausente preserva lotes das extensões 8.0/8.1 já publicadas. */
  runId?: string;
  /** ISO-2 usado como contexto para telefones sem código internacional. */
  country?: string;
  /** `false` grava e conta o lote sem concluir a etapa corrente do schedule. */
  stepComplete?: boolean;
}

export interface CaptureLeadBatch extends CaptureBatchBase {
  module: CaptureLeadModule;
  leads: CaptureRawRecord[];
  places?: never;
}

export interface CapturePlacesBatch extends CaptureBatchBase {
  module: 'places';
  places: CaptureRawRecord[];
  leads?: never;
}

export type CaptureBatch = CaptureLeadBatch | CapturePlacesBatch;

/** Envelope mínimo que permite correlacionar até mesmo um payload inválido. */
export interface CaptureBatchEnvelope {
  __captureBatch: true;
  /** Identificador estável entre tentativas do mesmo lote. */
  ackId: string;
  batch: unknown;
}

/** Mensagem que permanece na fila da extensão até receber um ACK positivo. */
export interface CaptureBatchMessage extends CaptureBatchEnvelope {
  batch: CaptureBatch;
}

export interface CaptureAckSuccess {
  ok: true;
  /** Quantidade realmente nova; pode ser zero em lote vazio ou repetido. */
  inserted: number;
  /** Total confirmado da lista depois da transação. */
  count: number;
}

export interface CaptureAckFailure {
  ok: false;
  error: CaptureAckErrorCode;
}

export type CaptureAckResult = CaptureAckSuccess | CaptureAckFailure;

export interface CaptureAckMessage {
  __captureAck: string;
  /** Eco do lote correlacionado; ausente mantém compatibilidade com os dois lados antigos. */
  runId?: string;
  result: CaptureAckResult;
}

export interface CaptureNavigateMessage {
  type: 'runtime';
  url: string;
  active: true;
  /** Presente somente no primeiro comando de uma nova captura; retomadas omitem. */
  beginCapture?: true;
  captureRunId?: string;
  /** Só a combinação protocolo + runId habilita os eventos do corte 2 na extensão. */
  captureProtocol?: typeof CAPTURE_EVENTS_PROTOCOL;
}

export interface CaptureStopMessage {
  type: 'setSync';
  play: false;
}

export interface CaptureSyncState {
  captureTarget: CaptureTarget;
  captureListId: string;
  captureRunId?: string;
  module: ScanType;
  play: boolean;
}

export const CAPTURE_DELIVERY_PAUSE_REASONS = [
  'invalid_batch',
  'email_dns_unavailable',
  'quota_exceeded',
  'storage_failed',
  'internal_error',
  'panel_closed',
  'delivery_unavailable',
  'delivery_failed',
] as const;

export type CaptureDeliveryPauseReason = (typeof CAPTURE_DELIVERY_PAUSE_REASONS)[number];

export interface CaptureStopRequestedMessage {
  type: 'stop_requested';
  runId: string;
  listId: string;
  reason: 'user_requested';
}

export interface CaptureDeliveryPausedMessage {
  type: 'delivery_paused';
  runId: string;
  listId: string;
  reason: CaptureDeliveryPauseReason;
}

export interface CaptureSearchTabClosedMessage {
  type: 'search_tab_closed';
  runId: string;
  listId: string;
  reason: 'search_tab_closed';
}

export type CaptureRuntimeEvent =
  | CaptureStopRequestedMessage
  | CaptureDeliveryPausedMessage
  | CaptureSearchTabClosedMessage;

export type ExtensionToPanelCaptureMessage = CaptureBatchMessage | CaptureRuntimeEvent;
export type PanelToExtensionCaptureMessage =
  | CaptureAckMessage
  | CaptureNavigateMessage
  | CaptureStopMessage;

const LEAD_MODULE_SET: ReadonlySet<string> = new Set(CAPTURE_LEAD_MODULES);
const ACK_ERROR_SET: ReadonlySet<string> = new Set(CAPTURE_ACK_ERROR_CODES);
const DELIVERY_PAUSE_REASON_SET: ReadonlySet<string> = new Set(CAPTURE_DELIVERY_PAUSE_REASONS);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isRawRecordArray(value: unknown): value is CaptureRawRecord[] {
  return Array.isArray(value) && value.every(isRecord);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

export function isCaptureBatch(value: unknown): value is CaptureBatch {
  if (!isRecord(value) || !isNonEmptyString(value['listId'])) return false;
  if (value['runId'] !== undefined && !isNonEmptyString(value['runId'])) return false;
  if (value['country'] !== undefined && !isNonEmptyString(value['country'])) return false;
  if (value['stepComplete'] !== undefined && typeof value['stepComplete'] !== 'boolean') {
    return false;
  }

  const module = value['module'];
  if (module === 'places') {
    return value['leads'] === undefined && isRawRecordArray(value['places']);
  }

  return (
    typeof module === 'string' &&
    LEAD_MODULE_SET.has(module) &&
    value['places'] === undefined &&
    isRawRecordArray(value['leads'])
  );
}

export function isCaptureBatchMessage(value: unknown): value is CaptureBatchMessage {
  return isCaptureBatchEnvelope(value) && isCaptureBatch(value.batch);
}

export function isCaptureBatchEnvelope(value: unknown): value is CaptureBatchEnvelope {
  return (
    isRecord(value) &&
    value['__captureBatch'] === true &&
    isNonEmptyString(value['ackId']) &&
    'batch' in value
  );
}

export function isCaptureAckMessage(value: unknown): value is CaptureAckMessage {
  if (!isRecord(value) || !isNonEmptyString(value['__captureAck'])) return false;
  if (value['runId'] !== undefined && !isNonEmptyString(value['runId'])) return false;
  const result = value['result'];
  if (!isRecord(result)) return false;

  if (result['ok'] === true) {
    return isNonNegativeInteger(result['inserted']) && isNonNegativeInteger(result['count']);
  }

  return (
    result['ok'] === false &&
    typeof result['error'] === 'string' &&
    ACK_ERROR_SET.has(result['error'])
  );
}

export function isCaptureRuntimeEvent(value: unknown): value is CaptureRuntimeEvent {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value['runId']) ||
    !isNonEmptyString(value['listId'])
  ) {
    return false;
  }

  if (value['type'] === 'stop_requested') return value['reason'] === 'user_requested';
  if (value['type'] === 'search_tab_closed') return value['reason'] === 'search_tab_closed';
  return (
    value['type'] === 'delivery_paused' &&
    typeof value['reason'] === 'string' &&
    DELIVERY_PAUSE_REASON_SET.has(value['reason'])
  );
}
